import React, { useRef, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Marquee from '../Marquee';
import SpacedHeading from '../SpacedHeading';
import { works } from '../../mock';
import { ArrowUpRight, X } from 'lucide-react';

export default function SelectedWorks() {
  const [selectedProject, setSelectedProject] = useState(null);

  return (
    <section id="works" className="relative bg-[var(--bg)] pt-20 md:pt-28 pb-0">
      <Marquee items={['Trabalhos', 'Trabalhos', 'Trabalhos', 'Trabalhos', 'Trabalhos']} speed={8} className="border-y border-[var(--border)]" />


      <div className="max-w-7xl mx-auto px-6 md:px-10 mt-20 md:mt-28">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-20 md:mb-28">
          <SpacedHeading className="text-4xl md:text-7xl lg:text-8xl">Trabalhos Selecionados</SpacedHeading>
          <a
            href="https://www.behance.net/mroliveira"
            target="_blank"
            rel="noopener noreferrer"
            className="self-start group inline-flex items-center gap-2 text-xs tracking-[0.3em] border border-[var(--border)] rounded-full px-5 py-3 hover:bg-white hover:text-black transition-colors"
          >
            VER TODOS OS TRABALHOS
            <ArrowUpRight size={14} strokeWidth={2} />
          </a>

        </div>

        <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-4 space-y-4">
          {works.map((w, i) => (
            <WorkCard key={w.id} work={w} index={i} onOpen={() => setSelectedProject(w)} />
          ))}
        </div>
      </div>

      <AnimatePresence>
        {selectedProject && (
          <Lightbox project={selectedProject} onClose={() => setSelectedProject(null)} />
        )}
      </AnimatePresence>
    </section>
  );
}

function WorkCard({ work, index, onOpen }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      layoutId={`card-${work.id}`}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
      className="block mb-4 break-inside-avoid group cursor-pointer"
      onClick={onOpen}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="relative rounded-[var(--card-radius)] overflow-hidden bg-[var(--card)] border border-[var(--border)] group-hover:ring-1 group-hover:ring-[var(--accent)]/30 group-hover:shadow-2xl transition-all duration-500">
        <div className={`relative overflow-hidden ${work.video ? (work.id === 8 ? 'aspect-square' : 'aspect-video') : ''}`}>
          <div className="w-full h-full transition-transform duration-700 group-hover:scale-105">
            {work.video ? (
              <>
                <img
                  src={work.image}
                  alt={work.title}
                  className="w-full h-auto block"
                  loading="lazy"
                  decoding="async"
                  width={600}
                  height={400}
                />
                {isHovered && (
                  <video
                    src={work.video}
                    poster={work.image}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full h-full object-cover absolute inset-0 z-10"
                  />
                )}
              </>
            ) : (
              <img
                src={work.image}
                alt={work.title}
                className="w-full h-auto block"
                loading="lazy"
                decoding="async"
                width={600}
                height={400}
              />
            )}
          </div>
          
          {/* Numeração */}
          <div className="absolute top-4 left-4 z-10">
            <span className="text-[10px] font-mono tracking-widest text-white/40 bg-black/20 backdrop-blur-sm px-2 py-1 rounded-md border border-white/5">
              {work.index}
            </span>
          </div>

          {/* Botão flutuante estilo "Collect" */}
          <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
            <div className="px-4 py-2 rounded-full bg-[var(--accent)] text-white text-[10px] font-bold tracking-wider uppercase shadow-lg flex items-center gap-2">
              Ver Projeto
              <ArrowUpRight size={12} strokeWidth={3} />
            </div>
          </div>
        </div>

        <div className="p-5">
          <h4 className="text-sm font-semibold text-[var(--fg)] group-hover:text-[var(--accent)] transition-colors duration-300">
            {work.title}
          </h4>
          <p className="text-[11px] text-[var(--muted)] mt-1.5 tracking-wide line-clamp-1">
            {work.tags}
          </p>
        </div>
      </div>
    </motion.div>
  );
}

function Lightbox({ project, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[2000] flex items-center justify-center p-4 md:p-10 bg-black/95 backdrop-blur-md"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Close Button Fixed */}
      <button
        onClick={onClose}
        className="fixed bottom-10 left-1/2 -translate-x-1/2 md:bottom-auto md:left-auto md:top-4 md:right-4 md:translate-x-0 z-[2010] text-white/50 hover:text-white transition-colors p-3 bg-black/20 hover:bg-black/40 rounded-full backdrop-blur-md border border-white/10"
      >
        <X size={24} />
      </button>

      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className={`relative w-full max-h-full flex flex-col items-center gap-6 ${project.id === 8 ? 'max-w-2xl' : 'max-w-6xl'}`}
      >
        <div className="relative rounded-2xl overflow-hidden w-full flex justify-center items-center" style={{ maxHeight: '75vh' }}>
          {project.video ? (
            <div className={`relative w-full h-full flex justify-center items-center ${project.id === 8 ? 'aspect-square' : 'aspect-video'}`}>
              <video
                src={project.video}
                poster={project.image}
                autoPlay
                muted
                loop
                playsInline
                className="w-full h-full object-contain absolute inset-0 z-10"
              />
            </div>
          ) : (
            <img
              src={project.image}
              alt={project.title}
              className="max-w-full max-h-[75vh] object-contain"
              decoding="async"
            />
          )}
        </div>

        <div className="text-center space-y-2">
          <h2 className="text-2xl md:text-4xl font-bold tracking-tight text-white">
            {project.title}
          </h2>
          <p className="text-sm md:text-base text-[var(--muted)] tracking-wider uppercase">
            {project.tags}
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}
